import { doc, getDoc, setDoc, increment, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase'

function toDateSafe(value) {
  if (!value) return new Date()
  if (value.toDate) return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

function toDateKey(value) {
  const d = toDateSafe(value)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function listDateKeysInRange(startDate, endDate) {
  const start = toDateSafe(startDate)
  const end = toDateSafe(endDate)
  const from = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const to = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  const keys = []
  const cursor = new Date(from)
  while (cursor.getTime() <= to.getTime()) {
    keys.push(toDateKey(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return keys
}

function emptyDailyRow(date) {
  return { date, totalAttempts: 0, totalScoreSum: 0, averageScore: 0 }
}

function toDailyRow(date, data) {
  const totalAttempts = Number(data?.totalAttempts || 0)
  const totalScoreSum = Number(data?.totalScoreSum || 0)
  return {
    date,
    totalAttempts,
    totalScoreSum,
    averageScore: totalAttempts > 0 ? Math.round(totalScoreSum / totalAttempts) : 0
  }
}

function aggregateRows(rows) {
  const totalAttempts = rows.reduce((sum, row) => sum + row.totalAttempts, 0)
  const totalScoreSum = rows.reduce((sum, row) => sum + row.totalScoreSum, 0)
  return {
    totalAttempts,
    totalScoreSum,
    averageScore: totalAttempts > 0 ? Math.round(totalScoreSum / totalAttempts) : 0
  }
}

export async function recordAttemptStats({ score = 0, teacherClassId = null, completedAt = null } = {}) {
  const dateKey = toDateKey(completedAt || new Date())
  const safeScore = Number(score || 0)

  const platformRef = doc(db, 'stats', 'daily', 'days', dateKey)
  await setDoc(platformRef, {
    date: dateKey,
    totalAttempts: increment(1),
    totalScoreSum: increment(safeScore),
    updatedAt: serverTimestamp()
  }, { merge: true })

  if (!teacherClassId) return
  const classRef = doc(db, 'stats', 'classes', 'byClass', teacherClassId, 'days', dateKey)
  await setDoc(classRef, {
    date: dateKey,
    teacherClassId,
    totalAttempts: increment(1),
    totalScoreSum: increment(safeScore),
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function getPlatformDailyStats(startDate, endDate) {
  const keys = listDateKeysInRange(startDate, endDate)
  const snapshots = await Promise.all(
    keys.map((dateKey) => getDoc(doc(db, 'stats', 'daily', 'days', dateKey)))
  )
  const rows = keys.map((date, index) => {
    const snap = snapshots[index]
    return snap.exists() ? toDailyRow(date, snap.data()) : emptyDailyRow(date)
  })
  return {
    rows,
    totals: aggregateRows(rows)
  }
}

export async function getClassDailyStats(classIds = [], startDate, endDate) {
  const normalizedIds = (classIds || []).filter(Boolean)
  if (normalizedIds.length === 0) {
    return {
      rows: [],
      totals: { totalAttempts: 0, totalScoreSum: 0, averageScore: 0 }
    }
  }

  const keys = listDateKeysInRange(startDate, endDate)
  const matrix = await Promise.all(
    normalizedIds.map(async (classId) => {
      const snaps = await Promise.all(
        keys.map((dateKey) => getDoc(doc(db, 'stats', 'classes', 'byClass', classId, 'days', dateKey)))
      )
      return snaps.map((snap, idx) => ({ date: keys[idx], data: snap.exists() ? snap.data() : null }))
    })
  )

  const rows = keys.map((date, dayIdx) => {
    let totalAttempts = 0
    let totalScoreSum = 0
    matrix.forEach((classRows) => {
      const data = classRows[dayIdx]?.data
      if (!data) return
      totalAttempts += Number(data.totalAttempts || 0)
      totalScoreSum += Number(data.totalScoreSum || 0)
    })
    return {
      date,
      totalAttempts,
      totalScoreSum,
      averageScore: totalAttempts > 0 ? Math.round(totalScoreSum / totalAttempts) : 0
    }
  })

  return {
    rows,
    totals: aggregateRows(rows)
  }
}
